import {
  UseMutationResult,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";

import { affiliate, getGetAffiliationStatusQueryKey } from "@/api/generated";
import type {
  AffiliateResponse,
  PartnerIntentRequest,
} from "@/api/generated/models";

/**
 * Refreshes the user's partner affiliation. Returns the structured outcome and
 * the user's complete post-call affiliation state, which is seeded into the
 * shared affiliation-status cache so downstream consumers (settings, etc.) see
 * the new state without a refetch.
 *
 * Pass partner_intent (from local storage) when the user has just been
 * referred by a known partner. Omit it (pass null) to broad-search for any
 * eligible membership affiliation — used by the sign-in flow to surface a
 * welcome screen if the user has become eligible between sign-ups.
 */
export const useAttemptAffiliation = (): UseMutationResult<
  AffiliateResponse,
  Error,
  PartnerIntentRequest | null
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (partnerIntent: PartnerIntentRequest | null) =>
      affiliate(partnerIntent ? { partner_intent: partnerIntent } : {}),
    onSuccess: (response) => {
      // The BE returns an empty status in a few "we don't actually know" cases
      // (FF off, pre-check fetch errored, post-claim refresh errored). Seeding
      // those into the cache would clobber previously-fetched affiliations.
      // Only seed when there's something to seed; otherwise invalidate so the
      // next consumer triggers a fresh fetch.
      if (response.status.results && response.status.results.length > 0) {
        queryClient.setQueryData(
          getGetAffiliationStatusQueryKey(),
          response.status,
        );
      } else {
        queryClient.invalidateQueries({
          queryKey: getGetAffiliationStatusQueryKey(),
        });
      }
    },
  });
};
